import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, lastValueFrom, Observable, Subject } from 'rxjs';
import { ConfigService } from './config.service';
import { parsePlaceAddress } from './mux-session.service';

export namespace MuxAgentService {
  export interface Agent {
    scoped_team?: string | null;   // team dir the agent lives in; null = unscoped (visible in all your teams)
    id: string;
    name: string;
    backend: string;
    model: string;
    system_prompt?: string;
    system_prompt_file?: string;
    heartbeat_prompt?: string;
    heartbeat_enabled: boolean;
    heartbeat_interval: number;
    temperature: number;
    max_tokens?: number;
    effort?: string;
    credentials_id?: string;
    mcp_servers: string[];
    mcp_server_overrides?: Record<string, any>;
    disabled_skills?: string[];
    heartbeat_stimuli?: any[];
    metadata: Record<string, any>;
  }

  export interface Skill {
    name: string;
    path: string;
  }

  export interface SkillsResponse {
    skills: Skill[];
  }

  export interface SkillsStatusResponse {
    available: Skill[];
    enabled: string[];
    disabled: string[];
  }

  export interface AgentsResponse {
    agents: Agent[];
  }

  export interface ModelsResponse {
    models: Record<string, ModelInfo[]>;
    /** Model allow-lists keyed 'vendor:mode' — a bound credential restricts the dropdown. */
    constraints?: Record<string, string[]>;
  }

  export interface ModelInfo {
    name: string;
    display_name?: string;
  }

  export interface McpServer {
    name: string;
    tools_count?: number;
    running?: boolean;
  }

  export interface McpServersResponse {
    servers: McpServer[];
  }

  export interface StimulusParameter {
    name: string;
    type: string;
    required: boolean;
    default?: any;
    description: string;
    placeholder?: string;
  }

  export interface StimulusPlugin {
    id: string;
    name: string;
    description: string;
    parameters: StimulusParameter[];
    system?: boolean;
  }

  export interface AgentStimulus {
    id: string;
    plugin: string;
    plugin_name: string;
    plugin_description?: string;
    params: Record<string, any>;
    enabled: boolean;
    label?: string;
    instruction?: string;
    last_triggered?: string;
  }
}

@Injectable({
  providedIn: 'root'
})
export class MuxAgentService {
  private agents: MuxAgentService.Agent[] = [];
  private currentAgentId: string = 'default';

  // Observable for agent changes
  private currentAgentSubject = new BehaviorSubject<string>('default');
  public currentAgent$ = this.currentAgentSubject.asObservable();

  // Observable for agents list changes (create/update/delete)
  private agentsChangedSubject = new Subject<void>();
  public agentsChanged$ = this.agentsChangedSubject.asObservable();

  constructor(private http: HttpClient) {
    // Load saved agent ID from localStorage
    const savedAgentId = localStorage.getItem('lit_current_agent_id');
    if (savedAgentId) {
      this.currentAgentId = savedAgentId;
      this.currentAgentSubject.next(savedAgentId);
    }
  }

  /**
   * Load all agents from lit-mux
   */
  async loadAgents(team?: string): Promise<MuxAgentService.Agent[]> {
    // Team-scoped agents: an omitted team means "the team I'm standing in",
    // not "local" — otherwise every unparameterized caller lists only the
    // unscoped legacy agents (2026-09-01).
    if (team === undefined) {
      try { team = JSON.parse(localStorage.getItem('me') || '{}')?.selectedTeamName || undefined; } catch { /* keep undefined */ }
    }
    const remote = parsePlaceAddress(team);
    if (remote) {
      // Agents on an attached desktop, over the pipe. Deliberately NOT cached
      // into this.agents — that cache backs local agent management surfaces.
      try {
        const resp = await lastValueFrom(this.http.get<{ agents: MuxAgentService.Agent[] }>(
          `${ConfigService.muxUrl}/remote/${encodeURIComponent(remote.device)}/agents?team=${encodeURIComponent(remote.team)}`));
        return resp?.agents || [];
      } catch (error) {
        console.error('Failed to load remote agents:', error);
        return [];
      }
    }
    try {
      let url = `${ConfigService.muxUrl}/agents`;
      if (team) url += `?team=${encodeURIComponent(team)}`;
      const response = await lastValueFrom(
        this.http.get<MuxAgentService.AgentsResponse>(url)
      );
      this.agents = response.agents || [];
      console.log(`Loaded ${this.agents.length} agents from lit-mux`);
      return this.agents;
    } catch (error) {
      console.error('Failed to load agents from lit-mux:', error);
      return [];
    }
  }

  /**
   * Get cached agents list
   */
  getAgents(): MuxAgentService.Agent[] {
    return this.agents;
  }

  /**
   * Get current agent
   */
  getCurrentAgent(): MuxAgentService.Agent | undefined {
    return this.agents.find(a => a.id === this.currentAgentId);
  }

  /**
   * Get current agent ID
   */
  getCurrentAgentId(): string {
    return this.currentAgentId;
  }

  /**
   * Set current agent and persist to localStorage
   */
  setCurrentAgent(agentId: string): void {
    this.currentAgentId = agentId;
    localStorage.setItem('lit_current_agent_id', agentId);
    this.currentAgentSubject.next(agentId);
    console.log(`Switched to agent: ${agentId}`);
  }

  /**
   * Get agent by ID from server
   */
  async getAgent(agentId: string): Promise<MuxAgentService.Agent | null> {
    try {
      return await lastValueFrom(
        this.http.get<MuxAgentService.Agent>(`${ConfigService.muxUrl}/agents/${agentId}`)
      );
    } catch (error) {
      console.error('Failed to get agent:', error);
      return null;
    }
  }

  /**
   * Create a new agent
   */
  async createAgent(config: Partial<MuxAgentService.Agent>, team?: string): Promise<MuxAgentService.Agent | null> {
    try {
      const result = await lastValueFrom(
        this.http.post<MuxAgentService.Agent>(`${ConfigService.muxUrl}/agents?team=${encodeURIComponent(team ?? (JSON.parse(localStorage.getItem('me') || '{}')?.selectedTeamName || 'local'))}`, config)
      );
      await this.loadAgents(); // Reload agent list
      this.agentsChangedSubject.next(); // Notify subscribers
      return result;
    } catch (error) {
      console.error('Failed to create agent:', error);
      throw error;
    }
  }

  /**
   * Update an existing agent
   */
  async updateAgent(agentId: string, config: Partial<MuxAgentService.Agent>, team?: string): Promise<MuxAgentService.Agent | null> {
    try {
      const result = await lastValueFrom(
        this.http.post<MuxAgentService.Agent>(`${ConfigService.muxUrl}/agents?team=${encodeURIComponent(team ?? (JSON.parse(localStorage.getItem('me') || '{}')?.selectedTeamName || 'local'))}`, config)
      );
      await this.loadAgents(); // Reload agent list
      this.agentsChangedSubject.next(); // Notify subscribers
      return result;
    } catch (error) {
      console.error('Failed to update agent:', error);
      throw error;
    }
  }

  /**
   * Delete an agent
   */
  async deleteAgent(agentId: string, deleteSessions: boolean = false): Promise<void> {
    try {
      await lastValueFrom(
        this.http.delete(`${ConfigService.muxUrl}/agents/${agentId}?delete_sessions=${deleteSessions}&team=${encodeURIComponent(JSON.parse(localStorage.getItem('me') || '{}')?.selectedTeamName || 'local')}`)
      );

      // If we deleted the current agent, switch to default
      if (agentId === this.currentAgentId) {
        this.setCurrentAgent('default');
      }

      await this.loadAgents(); // Reload agent list
      this.agentsChangedSubject.next(); // Notify subscribers
    } catch (error) {
      console.error('Failed to delete agent:', error);
      throw error;
    }
  }

  /**
   * Reload an agent's configuration from disk.
   * Useful when the agent's JSON file has been modified externally
   * (e.g., MCP credentials updated) and changes need to take effect.
   */
  async reloadAgent(agentId: string): Promise<{ status: string; agent: MuxAgentService.Agent } | null> {
    try {
      const result = await lastValueFrom(
        this.http.post<{ status: string; agent_id: string; message: string; agent: MuxAgentService.Agent }>(
          `${ConfigService.muxUrl}/agents/${agentId}/reload`,
          {}
        )
      );
      await this.loadAgents(); // Reload agent list to sync in-memory state
      this.agentsChangedSubject.next(); // Notify subscribers
      return { status: result.status, agent: result.agent };
    } catch (error) {
      console.error('Failed to reload agent:', error);
      throw error;
    }
  }

  /**
   * Generate a unique agent ID from the name
   * Slugifies the name and adds a suffix if there's a collision
   */
  generateAgentId(name: string): string {
    // Slugify: lowercase, replace non-alphanumeric with dashes, trim dashes
    let slug = name.toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    // Fallback if name was all special characters
    if (!slug) {
      slug = 'agent';
    }

    // Check for uniqueness among existing agents
    const existingIds = new Set(this.agents.map(a => a.id));

    if (!existingIds.has(slug)) {
      return slug;
    }

    // Add numeric suffix for uniqueness
    let counter = 2;
    while (existingIds.has(`${slug}-${counter}`)) {
      counter++;
    }
    return `${slug}-${counter}`;
  }

  /**
   * Get available models for all backends
   */
  /** Scope an unscoped agent into a team (the one-button migrator). */
  async scopeAgentToTeam(agentId: string, team: string): Promise<void> {
    await lastValueFrom(this.http.post(`${ConfigService.muxUrl}/agents/${encodeURIComponent(agentId)}/team`, { team }));
  }

  async getModels(team?: string): Promise<MuxAgentService.ModelsResponse | null> {
    try {
      // Team policy filters `/models?team=` to what THIS user may use; without
      // the team the catalog is unfiltered (docs/plans/org-policy.md).
      let t = team;
      if (t === undefined) {
        try { t = JSON.parse(localStorage.getItem('me') || '{}')?.selectedTeamName || undefined; } catch { t = undefined; }
      }
      const url = `${ConfigService.muxUrl}/models` + (t ? `?team=${encodeURIComponent(t)}` : '');
      return await lastValueFrom(
        this.http.get<MuxAgentService.ModelsResponse>(url)
      );
    } catch (error) {
      console.error('Failed to get models:', error);
      return null;
    }
  }

  /**
   * Get available MCP servers
   */
  async getMcpServers(): Promise<MuxAgentService.McpServer[]> {
    try {
      const response = await lastValueFrom(
        this.http.get<MuxAgentService.McpServersResponse>(`${ConfigService.muxUrl}/mcp/servers`)
      );
      return response.servers || [];
    } catch (error) {
      console.error('Failed to get MCP servers:', error);
      return [];
    }
  }

  /**
   * Get available skills from /etc/lit/skills/
   */
  async getSkills(): Promise<MuxAgentService.SkillsResponse | null> {
    try {
      return await lastValueFrom(
        this.http.get<MuxAgentService.SkillsResponse>(`${ConfigService.muxUrl}/skills`)
      );
    } catch (error) {
      console.error('Failed to get skills:', error);
      return null;
    }
  }

  /**
   * Get actual skill status by checking symlinks on disk.
   */
  async getSkillsStatus(): Promise<MuxAgentService.SkillsStatusResponse | null> {
    try {
      return await lastValueFrom(
        this.http.get<MuxAgentService.SkillsStatusResponse>(`${ConfigService.muxUrl}/skills/status`)
      );
    } catch (error) {
      console.error('Failed to get skills status:', error);
      return null;
    }
  }

  /**
   * Get the default system prompt for new agents.
   * The prompt includes memory instructions personalized for the current user.
   */
  async getDefaultPrompt(model: string = 'claude'): Promise<string | null> {
    try {
      const response = await lastValueFrom(
        this.http.get<{ prompt: string }>(`${ConfigService.muxUrl}/agents/default-prompt?model=${encodeURIComponent(model)}`)
      );
      return response.prompt;
    } catch (error) {
      console.error('Failed to get default prompt:', error);
      return null;
    }
  }

  async getAvailableStimuliPlugins(): Promise<MuxAgentService.StimulusPlugin[]> {
    try {
      const response = await lastValueFrom(
        this.http.get<{ stimuli: MuxAgentService.StimulusPlugin[] }>(`${ConfigService.muxUrl}/stimuli`)
      );
      return response.stimuli || [];
    } catch (error) {
      console.error('Failed to get stimulus plugins:', error);
      return [];
    }
  }

  async getAgentStimuli(agentId: string): Promise<MuxAgentService.AgentStimulus[]> {
    try {
      const response = await lastValueFrom(
        this.http.get<{ stimuli: MuxAgentService.AgentStimulus[] }>(`${ConfigService.muxUrl}/agents/${agentId}/stimuli`)
      );
      return response.stimuli || [];
    } catch (error) {
      console.error('Failed to get agent stimuli:', error);
      return [];
    }
  }

  /**
   * Get the primary agent ID for the current user.
   */
  async getPrimaryAgent(): Promise<string | null> {
    try {
      const response = await lastValueFrom(
        this.http.get<{ primary_agent: string | null }>(`${ConfigService.muxUrl}/agents/primary`)
      );
      return response.primary_agent;
    } catch (error) {
      console.error('Failed to get primary agent:', error);
      return null;
    }
  }

  /**
   * Set or clear the primary agent for the current user.
   * Pass null to clear the primary agent designation.
   */
  async setPrimaryAgent(agentId: string | null): Promise<void> {
    try {
      await lastValueFrom(
        this.http.put(`${ConfigService.muxUrl}/agents/primary`, { agent_id: agentId })
      );
    } catch (error) {
      console.error('Failed to set primary agent:', error);
      throw error;
    }
  }
}
